import React, { useState } from "react";
import { Switch } from "@headlessui/react";

const PersonalDetailsPage = ({ onNext, onBack, formData: initialFormData }) => {
  const [personalData, setPersonalData] = useState({
    name: initialFormData?.name || "",
    surname: initialFormData?.surname || "",
    street: initialFormData?.street || "",
    houseNumber: initialFormData?.houseNumber || "",
    postalCode: initialFormData?.postalCode || "",
    country: initialFormData?.country || "",
    birthdate: initialFormData?.birthdate || "",
    phoneNumber: initialFormData?.phoneNumber || "",
    isLegalEntity: initialFormData?.isLegalEntity || false,
    enterpriseNumber: initialFormData?.enterpriseNumber || "",
    legalForm: initialFormData?.legalForm || "",
    email: initialFormData?.email || "",
    noAccidents: initialFormData?.noAccidents || false,
    accidentDetails: initialFormData?.accidentDetails || [],
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPersonalData((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors({ ...errors, [name]: "" });
  };

  const handleAddAccident = () => {
    setPersonalData((prevState) => ({
      ...prevState,
      accidentDetails: [
        ...prevState.accidentDetails,
        { boatName: "", date: "", circumstances: "", costs: "" },
      ],
    }));
  };

  const handleAccidentChange = (index, field, value) => {
    const updatedAccidents = [...personalData.accidentDetails];
    updatedAccidents[index][field] = value;
    setPersonalData((prevState) => ({
      ...prevState,
      accidentDetails: updatedAccidents,
    }));
    setErrors({ ...errors, accidentDetails: "" });
  };

  const handleTextChange = (e, field) => {
    const value = e.target.value;
    const nameRegex = /^[A-Za-zÀ-ÿ\s'-]+$/; // Alleen letters, spaties, apostrofs en koppeltekens toegestaan

    if (value === "" || nameRegex.test(value)) {
      setPersonalData({ ...personalData, [field]: value });
      setErrors({ ...errors, [field]: "" });
    } else {
      setErrors({ ...errors, [field]: "Gebruik alleen letters en spaties." });
    }
  };

  const handlePostalCodeChange = (e) => {
    const value = e.target.value;
    const postalCodeRegex = /^\d*$/; // Alleen cijfers toegestaan

    if (value === "" || postalCodeRegex.test(value)) {
      setPersonalData({ ...personalData, postalCode: value });
      setErrors({ ...errors, postalCode: "" });
    } else {
      setErrors({ ...errors, postalCode: "Alleen cijfers zijn toegestaan." });
    }
  };

  const handleRemoveAccident = (indexToRemove) => {
    const updatedAccidents = personalData.accidentDetails.filter(
      (_, index) => index !== indexToRemove
    );

    setPersonalData((prevState) => ({
      ...prevState,
      accidentDetails: updatedAccidents,
      noAccidents: updatedAccidents.length > 0, // Zet op false als lijst leeg is
    }));
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    const phoneRegex = /^[0-9()+\s\-\/]*$/; // Nu toegestaan: cijfers, +, (), spaties, -, en /

    if (value === "" || phoneRegex.test(value)) {
      setPersonalData({ ...personalData, phoneNumber: value });
      setErrors({ ...errors, phoneNumber: "" });
    } else {
      setErrors({
        ...errors,
        phoneNumber:
          "Ongeldig telefoonnummer. Gebruik alleen cijfers, '+', '(', ')', '-', of '/'",
      });
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    setPersonalData({ ...personalData, email: value });

    if (value === "" || emailRegex.test(value)) {
      setErrors({ ...errors, email: "" });
    } else {
      setErrors({
        ...errors,
        email: "Ongeldig e-mailadres. Gebruik het formaat naam@domein.ext",
      });
    }
  };

  const handleNext = () => {
    const newErrors = {};

    if (!personalData.name) newErrors.name = "Dit veld is verplicht.";
    if (!personalData.surname) newErrors.surname = "Dit veld is verplicht.";
    if (!personalData.street) newErrors.street = "Dit veld is verplicht.";
    if (!personalData.houseNumber)
      newErrors.houseNumber = "Dit veld is verplicht.";
    if (!personalData.postalCode)
      newErrors.postalCode = "Dit veld is verplicht.";
    if (!personalData.country) newErrors.country = "Dit veld is verplicht.";
    if (!personalData.birthdate) newErrors.birthdate = "Dit veld is verplicht.";
    if (!personalData.phoneNumber)
      newErrors.phoneNumber = "Dit veld is verplicht.";
    if (!personalData.email) newErrors.email = "Dit veld is verplicht.";

    if (personalData.isLegalEntity) {
      if (!personalData.enterpriseNumber)
        newErrors.enterpriseNumber = "Dit veld is verplicht.";
      if (!personalData.legalForm)
        newErrors.legalForm = "Dit veld is verplicht.";

      if (personalData.addLegalAddress) {
        if (!personalData.legalStreet)
          newErrors.legalStreet = "Dit veld is verplicht.";
        if (!personalData.legalHouseNumber)
          newErrors.legalHouseNumber = "Dit veld is verplicht.";
        if (!personalData.legalPostalCode)
          newErrors.legalPostalCode = "Dit veld is verplicht.";
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext(personalData);
    }
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-3xl font-semibold mb-6 text-left text-[#1a2a6c]">
          Persoonlijke gegevens
        </h1>
        <p className="text-lg font-medium text-[#1a2a6c] mb-6">
          Bezorg ons je persoonlijke gegevens dan gaan wij aan de slag voor je
          offerte op maat
        </p>
        <form className="space-y-6">
          {/* Naam en Voornaam */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Naam
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={personalData.name}
                onChange={(e) => handleTextChange(e, "name")}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="surname"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Voornaam
              </label>
              <input
                id="surname"
                type="text"
                name="surname"
                value={personalData.surname}
                onChange={(e) => handleTextChange(e, "surname")}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.surname && (
                <p className="text-sm text-red-600 mt-1">{errors.surname}</p>
              )}
            </div>
          </div>

          {/* Adres en Land */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-[#1a2a6c]">
                Land
              </label>
              <select
                name="country"
                value={personalData.country}
                onChange={handleChange}
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md"
              >
                <option value="">Selecteer een land</option>
                <option value="België">België</option>
                <option value="Nederland">Nederland</option>
                <option value="Duitsland">Duitsland</option>
                <option value="Frankrijk">Frankrijk</option>
              </select>
              {errors.country && (
                <p className="text-sm text-red-600">{errors.country}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1a2a6c]">
                Postcode
              </label>
              <input
                type="text"
                name="postalCode"
                value={personalData.postalCode}
                onChange={handlePostalCodeChange}
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md"
              />
              {errors.postalCode && (
                <p className="text-sm text-red-600">{errors.postalCode}</p>
              )}
            </div>
          </div>

          {/* Adres: Straat, Nummer, Postcode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="street"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Straat
              </label>
              <input
                id="street"
                type="text"
                name="street"
                value={personalData.street}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.surname && (
                <p className="text-sm text-red-600 mt-1">{errors.street}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="houseNumber"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Nummer (+ busnummer)
              </label>
              <input
                id="houseNumber"
                type="text"
                name="houseNumber"
                value={personalData.houseNumber}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.surname && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.houseNumber}
                </p>
              )}
            </div>
          </div>

          {/* Geboortedatum en Telefoonnummer */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="birthdate"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Geboortedatum
              </label>
              <input
                id="birthdate"
                type="date"
                name="birthdate"
                value={personalData.birthdate}
                onChange={handleChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.surname && (
                <p className="text-sm text-red-600 mt-1">{errors.birthdate}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-semibold text-[#1a2a6c]"
              >
                Telefoonnummer
              </label>
              <input
                id="phoneNumber"
                type="tel"
                name="phoneNumber"
                value={personalData.phoneNumber}
                onChange={handlePhoneNumberChange}
                required
                className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
              />
              {errors.phoneNumber && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.phoneNumber}
                </p>
              )}
            </div>
          </div>

          {/* Rechtspersoon */}
          <div className="flex items-center justify-between">
            <label className="text-lg font-semibold text-[#1a2a6c]">
              Rechtspersoon?
            </label>
            <Switch
              checked={personalData.isLegalEntity}
              onChange={(checked) =>
                setPersonalData({ ...personalData, isLegalEntity: checked })
              }
              className={`${
                personalData.isLegalEntity ? "bg-[#1a2a6c]" : "bg-gray-200"
              } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span
                className={`${
                  personalData.isLegalEntity
                    ? "translate-x-6 bg-[#ff6f61]"
                    : "translate-x-1 bg-white"
                } inline-block h-4 w-4 transform rounded-full transition`}
              />
            </Switch>
          </div>

          {personalData.isLegalEntity && (
            <>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-[#1a2a6c]">
                    Ondernemingsnummer
                  </label>
                  <input
                    type="text"
                    name="enterpriseNumber"
                    value={personalData.enterpriseNumber}
                    onChange={handleChange}
                    required
                    className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1a2a6c]">
                    Rechtsvorm
                  </label>
                  <input
                    type="text"
                    name="legalForm"
                    value={personalData.legalForm}
                    onChange={handleChange}
                    required
                    className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md"
                  />
                </div>
              </div>

              {/* Adres van rechtspersoon toevoegen */}
              <div className="flex items-center justify-between mt-4">
                <label className="text-lg font-semibold text-[#1a2a6c]">
                  Adres van rechtspersoon toevoegen?
                </label>
                <Switch
                  checked={personalData.addLegalAddress}
                  onChange={(checked) =>
                    setPersonalData({
                      ...personalData,
                      addLegalAddress: checked,
                    })
                  }
                  className={`${
                    personalData.addLegalAddress
                      ? "bg-[#1a2a6c]"
                      : "bg-gray-200"
                  } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      personalData.addLegalAddress
                        ? "translate-x-6 bg-[#ff6f61]"
                        : "translate-x-1 bg-white"
                    } inline-block h-4 w-4 transform rounded-full transition`}
                  />
                </Switch>
              </div>

              {personalData.addLegalAddress && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-4">
                  {/* Straat */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1a2a6c]">
                      Straat
                    </label>
                    <input
                      type="text"
                      name="legalStreet"
                      value={personalData.legalStreet}
                      onChange={handleChange}
                      required
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                    {errors.legalStreet && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.legalStreet}
                      </p>
                    )}
                  </div>

                  {/* Nummer */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1a2a6c]">
                      Nummer (+ busnummer)
                    </label>
                    <input
                      type="text"
                      name="legalHouseNumber"
                      value={personalData.legalHouseNumber}
                      onChange={handleChange}
                      required
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                    {errors.legalHouseNumber && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.legalHouseNumber}
                      </p>
                    )}
                  </div>

                  {/* Postcode */}
                  <div>
                    <label className="block text-sm font-semibold text-[#1a2a6c]">
                      Postcode
                    </label>
                    <input
                      type="text"
                      name="legalPostalCode"
                      value={personalData.legalPostalCode}
                      onChange={handleChange}
                      required
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                    {errors.legalPostalCode && (
                      <p className="text-sm text-red-600 mt-1">
                        {errors.legalPostalCode}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold text-[#1a2a6c]"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={personalData.email}
              onChange={handleEmailChange}
              required
              className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
            />
            {errors.surname && (
              <p className="text-sm text-red-600 mt-1">{errors.email}</p>
            )}
          </div>

          {/* Geen schadegevallen Ja/Nee */}
          <div className="flex items-center justify-between">
            <label className="text-lg font-semibold text-[#1a2a6c]">
              Heb je in de laatste 5 jaar een schadegeval gehad?
            </label>
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() =>
                  setPersonalData({ ...personalData, noAccidents: true })
                }
                className={`px-4 py-2 rounded-md ${
                  personalData.noAccidents === true
                    ? "bg-[#ff6f61] text-white"
                    : "bg-gray-300"
                }`}
              >
                Ja
              </button>
              <button
                type="button"
                onClick={() =>
                  setPersonalData({ ...personalData, noAccidents: false })
                }
                className={`px-4 py-2 rounded-md ${
                  personalData.noAccidents === false
                    ? "bg-[#1a2a6c] text-white"
                    : "bg-gray-300"
                }`}
              >
                Nee
              </button>
            </div>
          </div>
          {errors.noAccidents && (
            <p className="text-sm text-red-600">{errors.noAccidents}</p>
          )}

          {/* Schadegevallen details */}
          {personalData.noAccidents && (
            <div className="mt-4 space-y-4">
              <h3 className="text-xl font-semibold text-[#1a2a6c]">
                Schadegevallen
              </h3>
              {personalData.accidentDetails.map((accident, index) => (
                <div
                  key={index}
                  className="space-y-4 border p-4 rounded-lg border-[#ff6f61]"
                >
                  <div>
                    <label
                      htmlFor={`boatName${index}`}
                      className="block text-sm font-semibold text-[#1a2a6c]"
                    >
                      Naam vaartuig
                    </label>
                    <input
                      id={`boatName${index}`}
                      type="text"
                      value={accident.boatName}
                      onChange={(e) =>
                        handleAccidentChange(index, "boatName", e.target.value)
                      }
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`date${index}`}
                      className="block text-sm font-semibold text-[#1a2a6c]"
                    >
                      Datum van het ongeval
                    </label>
                    <input
                      id={`date${index}`}
                      type="date"
                      value={accident.date}
                      onChange={(e) =>
                        handleAccidentChange(index, "date", e.target.value)
                      }
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`circumstances${index}`}
                      className="block text-sm font-semibold text-[#1a2a6c]"
                    >
                      Omstandigheden
                    </label>
                    <textarea
                      id={`circumstances${index}`}
                      value={accident.circumstances}
                      onChange={(e) =>
                        handleAccidentChange(
                          index,
                          "circumstances",
                          e.target.value
                        )
                      }
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`costs${index}`}
                      className="block text-sm font-semibold text-[#1a2a6c]"
                    >
                      Kosten
                    </label>
                    <input
                      id={`costs${index}`}
                      type="number"
                      value={accident.costs}
                      onChange={(e) =>
                        handleAccidentChange(index, "costs", e.target.value)
                      }
                      className="mt-1 p-2 w-full border border-[#ff6f61] rounded-md focus:ring-2 focus:ring-[#ff6f61]"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveAccident(index)}
                    className="mt-2 text-sm text-red-600 hover:underline"
                  >
                    Verwijder dit schadegeval
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddAccident}
                className="mt-4 py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md hover:bg-[#142057]"
              >
                Voeg ongeval toe
              </button>
            </div>
          )}
          <div className="flex justify-between mt-6">
            <button
              type="button"
              onClick={onBack}
              className="py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md hover:bg-[#142057]"
            >
              Terug
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md hover:bg-[#142057]"
            >
              Volgende
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PersonalDetailsPage;
